import React from 'react'
import { BasicInfoWrapperProps } from '../BasicInfo'
import LivingSpaceField from '../fields/livingSpaceField/LivingSpaceField'
import MainFloorSpaceField, { MainFloorType } from '../fields/mainFloorSpaceField/MainFloorSpaceField'
import PlotAreaField from '../fields/plotAreaField/PlotAreaField'
import PriceField, { PriceFieldType } from '../fields/priceField/PriceField'
import PriceTypeField from '../fields/priceTypeField/PriceTypeField'
import RoomField, { RoomFieldProps } from '../fields/roomField/RoomField'
import RoomSizeField from '../fields/roomSizeField/RoomSizeField'
import SeparableSpaceField, { SeparableSpaceType } from '../fields/separableSpaceField/SeparableSpaceField'
import TotalFloorSpaceField from '../fields/totalFloorSpaceField/TotalFloorSpaceField'
import PriceMaximizer from '../../../../../common/display/priceMaximizer/PriceMaximizer'
import { BuildingTypeOption } from '../../../../../../@types/dropdown-types'
import BuildingType from '../../commonFields/buildingType/BuildingType'
import BathroomField from '../../commonFields/bathroom/BathroomField'
import ConstructionConditionField from '../../construction/fields/constructionConditionField/ConstructionConditionField'
import ConstructionYearField from '../../commonFields/constructionYearField/ConstructionYearField'
import { ConstructionScreenValidationResult } from '../../../../../../@types/validation-types'
import LastModernizationField from '../../commonFields/lastModernizationField/LastModernizationField'
import { getDefaultClasses, GetDefaultClassesOptions, Variant } from '../../../../../../utils/style-utils'
import { isWebView } from '../../../../../../utils/navigation'
import AuxiliaryAreaField from '../fields/auxiliaryAreaField/AuxiliaryAreaField'
import StartRentalDate from '../fields/startRentalDate/StartRentalDate'
import AdditionalCosts from '../../costs/fields/additionalCosts/AdditionalCosts'
import Deposit from '../../costs/fields/deposit/Deposit'
import PriceOnRequestToggle from '../fields/priceOnRequestToggle/PriceOnRequestToggle'
import CourtageGroup from '../../costs/fields/courtageGroup/CourtageGroup'
import StartRentalDateWithToggle, {
  StartRentalDateWithToggleProps,
} from '../fields/startRentalDateWithToggle/StartRentalDateWithToggle'
import PricePeriodTypeField from '../fields/pricePeriodTypeField/PricePeriodTypeField'

export const styleWrapper = (children: JSX.Element, variant: Variant = 'full', options?: GetDefaultClassesOptions) => (
  <div className={getDefaultClasses(variant, options)}>{children}</div>
)
export const livingSpaceField =
  (variant: Variant = 'left', options?: GetDefaultClassesOptions, showLabelWithTooltip?: boolean) =>
  (props: BasicInfoWrapperProps) => {
    const shouldShowTooltip = showLabelWithTooltip || isWebView()
    return styleWrapper(<LivingSpaceField {...props} showToolTipInLabel={shouldShowTooltip} />, variant, options)
  }

export const mainFloorSpaceField =
  (mainFloorType: MainFloorType, variant: Variant = 'leftOnAll', options?: GetDefaultClassesOptions) =>
  (props: BasicInfoWrapperProps) =>
    styleWrapper(<MainFloorSpaceField {...props} mainFloorType={mainFloorType} />, variant, options)

export const plotAreaField =
  (variant: Variant = 'full', options?: GetDefaultClassesOptions) =>
  (props: BasicInfoWrapperProps) =>
    styleWrapper(<PlotAreaField {...props} />, variant, options)

export const priceField =
  (priceFieldType: PriceFieldType, wrapperClassName?: string) => (props: BasicInfoWrapperProps) => (
    <PriceField {...props} priceFieldType={priceFieldType} wrapperClassName={wrapperClassName} />
  )

export const priceTypeField = () => (props: BasicInfoWrapperProps) => <PriceTypeField {...props} />
export const pricePeriodTypeField = () => (props: BasicInfoWrapperProps) => <PricePeriodTypeField {...props} />

export const purchasePriceOnRequestToggle = (variant?: Variant) => (props: BasicInfoWrapperProps) =>
  styleWrapper(
    <PriceOnRequestToggle
      {...props}
      selectedPriceType={'REQUEST_PURCHASE_PRICE'}
      unselectedPriceType={'PURCHASE_PRICE'}
    />,
    variant
  )

export const rentPriceOnRequestToggle = (variant?: Variant) => (props: BasicInfoWrapperProps) =>
  styleWrapper(
    <PriceOnRequestToggle {...props} selectedPriceType={'REQUEST_RENT_PRICE'} unselectedPriceType={null} />,
    variant
  )

export const roomField =
  (wrapperClassName?: string, labelKey?: string, showLabelWithTooltip?: boolean) => (props: BasicInfoWrapperProps) => {
    const shouldShowTooltip = showLabelWithTooltip || isWebView()
    const roomProps: RoomFieldProps = {
      ...props,
      wrapperClassName,
      labelKey,
    }
    return <RoomField {...roomProps} showLabelWithTooltip={shouldShowTooltip} />
  }

export const roomSizeField = (wrapperClassName?: string) => (props: BasicInfoWrapperProps) => (
  <RoomSizeField {...props} wrapperClassName={wrapperClassName} />
)

export const separableSpaceField =
  (separableSpaceType: SeparableSpaceType, variant: Variant = 'rightOnAll', options?: GetDefaultClassesOptions) =>
  (props: BasicInfoWrapperProps) =>
    styleWrapper(<SeparableSpaceField {...props} separableSpaceType={separableSpaceType} />, variant, options)

export const totalFloorSpaceField =
  (variant: Variant = 'left', options?: GetDefaultClassesOptions) =>
  (props: BasicInfoWrapperProps) =>
    styleWrapper(<TotalFloorSpaceField {...props} />, variant, options)

export const priceMaximizer = () => (props: BasicInfoWrapperProps) => (
  <PriceMaximizer insertionData={props.insertionData} />
)

export const buildingType =
  (buildingTypeOptions: BuildingTypeOption[], wrapperClassName?: string) => (props: BasicInfoWrapperProps) => (
    <BuildingType {...props} buildingTypeOptions={buildingTypeOptions} wrapperClassName={wrapperClassName} />
  )

export const bathroomField = (wrapperClassName?: string) => (props: BasicInfoWrapperProps) => (
  <BathroomField {...props} wrapperClassName={wrapperClassName} />
)

export const constructionConditionField =
  (wrapperClassName: string = 'palm-margin-top-l lap-margin-top-l desk-padding-left-m') =>
  (props: BasicInfoWrapperProps) => <ConstructionConditionField {...props} additionalClassNames={wrapperClassName} />

export const courtageGroup =
  (variant: Variant = 'full') =>
  (props: BasicInfoWrapperProps) =>
    styleWrapper(<CourtageGroup {...props} />, variant)

export const constructionYearField = () => (props: BasicInfoWrapperProps) => (
  <ConstructionYearField
    {...{
      ...props,
      validationMessages: props.validationMessages as ConstructionScreenValidationResult,
    }}
  />
)

export const lastModernizationField = () => (props: BasicInfoWrapperProps) => (
  <LastModernizationField
    {...{
      ...props,
      wrapperClassName: getDefaultClasses('rightOnDesk'),
      validationMessages: props.validationMessages as ConstructionScreenValidationResult,
    }}
  />
)

export const auxiliaryAreaField =
  (variant: Variant = 'full') =>
  (props: BasicInfoWrapperProps) =>
    styleWrapper(<AuxiliaryAreaField {...props} />, variant)

export const startRentalDateField = (wrapperClassName?: string) => (props: BasicInfoWrapperProps) => (
  <StartRentalDate {...props} wrapperClassName={wrapperClassName} />
)

export const startRentalDateWithToggleField = (wrapperClassName?: string) => (props: BasicInfoWrapperProps) => {
  const startRentalDateProps: StartRentalDateWithToggleProps = {
    ...props,
    wrapperClassName,
    doReset: props.doReset!,
  }
  return <StartRentalDateWithToggle {...startRentalDateProps} />
}

export const additionalCostsField =
  (variant: Variant = 'left', label: string = 'proRatedAdditionalCosts') =>
  (props: BasicInfoWrapperProps) =>
    styleWrapper(<AdditionalCosts label={label} {...props} />, variant)

export const depositField =
  (variant: Variant = 'right') =>
  (props: BasicInfoWrapperProps) =>
    styleWrapper(<Deposit {...props} wrapperClassName='grid-item one-whole' />, variant)
